<!-- src/apps/secret/components/receipt/ExpiryRing.vue -->

<script setup lang="ts">
  import { useI18n } from 'vue-i18n';
  import { usePreferredReducedMotion } from '@vueuse/core';
  import { computed, onMounted, onUnmounted, ref } from 'vue';

  // Countdown ring from the Figma Make receipt mock: an SVG circle whose
  // stroke fills with the elapsed fraction of the secret's lifetime, days
  // remaining in the center and an h/m/s clock ticking below (static when
  // the user prefers reduced motion).
  interface Props {
    createdAt: Date;
    expiresAt: Date;
  }

  const props = defineProps<Props>();
  const { t } = useI18n();

  const reducedMotion = usePreferredReducedMotion();
  const now = ref(Date.now());
  let timer: ReturnType<typeof setInterval> | undefined;

  onMounted(() => {
    now.value = Date.now();
    if (reducedMotion.value !== 'reduce') {
      timer = setInterval(() => {
        now.value = Date.now();
      }, 1000);
    }
  });

  onUnmounted(() => {
    if (timer) clearInterval(timer);
  });

  const DAY_MS = 86_400_000;
  const HOUR_MS = 3_600_000;
  const MINUTE_MS = 60_000;

  const totalMs = computed(() =>
    Math.max(props.expiresAt.getTime() - props.createdAt.getTime(), 1)
  );
  const remainingMs = computed(() => Math.max(props.expiresAt.getTime() - now.value, 0));
  const elapsedFraction = computed(() =>
    Math.min(Math.max((now.value - props.createdAt.getTime()) / totalMs.value, 0), 1)
  );

  const daysRemaining = computed(() => Math.floor(remainingMs.value / DAY_MS));
  const clock = computed(() => {
    const rest = remainingMs.value % DAY_MS;
    const pad = (n: number) => String(n).padStart(2, '0');
    const h = Math.floor(rest / HOUR_MS);
    const m = Math.floor((rest % HOUR_MS) / MINUTE_MS);
    const s = Math.floor((rest % MINUTE_MS) / 1000);
    return `${pad(h)}h ${pad(m)}m ${pad(s)}s`;
  });

  // SVG geometry: 96px viewBox, stroke 2, ring radius leaves room for the glow.
  const RADIUS = 45;
  const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
  const dashOffset = computed(() => CIRCUMFERENCE * (1 - elapsedFraction.value));

  const ariaLabel = computed(
    () => `${t('receipt_terminal.ring_time_remaining')}: ${daysRemaining.value}d ${clock.value}`
  );
</script>

<template>
  <div
    class="flex flex-col items-center gap-2"
    role="timer"
    aria-live="off"
    :aria-label="ariaLabel">
    <div class="relative size-24">
      <svg
        class="size-full -rotate-90"
        viewBox="0 0 96 96"
        aria-hidden="true">
        <!-- track -->
        <circle
          cx="48"
          cy="48"
          :r="RADIUS"
          fill="none"
          stroke-width="2"
          class="stroke-gray-200 dark:stroke-white/[0.13]" />
        <!-- elapsed progress -->
        <circle
          cx="48"
          cy="48"
          :r="RADIUS"
          fill="none"
          stroke-width="2"
          stroke-linecap="round"
          class="stroke-brand-500 drop-shadow-[0_0_3px_var(--color-brand-500)]"
          :style="reducedMotion !== 'reduce' ? 'transition: stroke-dashoffset 1s linear' : undefined"
          :stroke-dasharray="CIRCUMFERENCE"
          :stroke-dashoffset="dashOffset" />
      </svg>
      <div class="absolute inset-0 flex flex-col items-center justify-center">
        <span class="font-mono text-[22px] font-bold leading-none text-day-ink dark:text-night-ink">
          {{ daysRemaining }}
        </span>
        <!-- prettier-ignore-attribute class -->
        <span
          class="mt-1 font-mono text-[9px] uppercase tracking-[0.08em]
            text-day-ink-faint dark:text-night-ink-faint">
          {{ t('receipt_terminal.ring_days') }}
        </span>
      </div>
    </div>
    <span class="font-mono text-[11px] tabular-nums text-day-ink-muted dark:text-night-ink-muted">
      {{ clock }}
    </span>
  </div>
</template>
